import { CSSProperties, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
    AllCommunityModule,
    CellClickedEvent,
    ColDef,
    ColGroupDef,
    FirstDataRenderedEvent,
    GridApi,
    ModuleRegistry,
    RowClickedEvent,
    themeAlpine,
    IsFullWidthRowParams,
    ICellRendererParams,
    GetRowIdFunc
} from 'ag-grid-community';
import { GridCellListView } from './grid-cell-list.view';

import './grid.styles.scss';
import { GridCheckboxView } from './grid-checkbox.view';
import { LoadIndicatorView } from '../load-indicator/load-indicator.view';
import { GridCellLargeTextView } from './grid-cell-large-text.view';
import { LoadIndicatorSizeEnum } from '@vivli/shared/infrastructure/enum';
import { GridCellDateTimeView } from './grid-cell-date-time.view';
import { GridCellDateView } from './grid-cell-date.view';
import { DTIGrid } from '@vivli/shared/infrastructure/constants';
import { GridCellLongTextView } from './grid-cell-long-text.view';
import { GridCustomDisplayView } from './grid-custom-display.view';
import { GridCellLargeTextScrollView } from './grid-cell-large-text-scroll.view';
import { GridCellErrorIconView } from './grid-cell-error-icon.view';
import { GridCellYesNoView } from './grid-cell-yes-no.view';
import { GridSliderView } from './grid-slider.view';

// Register the module
ModuleRegistry.registerModules([AllCommunityModule]);

const agGridTheme = themeAlpine.withParams({ borderColor: '#e9eaef', backgroundColor: 'white' });

const gridContainerStyle: CSSProperties = {
    height: '100%',
    width: '100%',
    position: 'relative'
};

const gridOverlayStyle = (): CSSProperties => ({
    margin: 'auto',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
});

interface GridComponentProps {
    rowData: any[];
    columns: (ColDef | ColGroupDef)[];
    onRowClick?: (data: any) => void;
    onGridReady?: (gridApi: GridApi) => void;
    onFirstDataRendered?: (event: FirstDataRenderedEvent) => void;
    customComponents?: { component: any; name: string }[];
    fullWidthColumns?: boolean;
    style?: CSSProperties;
    hideHeader?: boolean;
    hideFloatingFilter?: boolean;
    emptyMessage?: string;
    rowHeight?: number;
    autoHeight?: boolean;
    pagination?: boolean;
    pageSize?: number;
    onRowCountChange?: (count: number) => void;
    rowSelection?: 'single' | 'multiple';
    highlightSelectedRow?: boolean;
    isLoading?: boolean;
    flushPrevious?: boolean;
    ignoreClickForFields?: string[];
    useFullWidthRows?: boolean;
    fullWidthCellRenderer?: (params: ICellRendererParams) => ReactElement;
    rowStyle?: CSSProperties;
    getRowId?: GetRowIdFunc<any>;
}

export const GridView = ({
    rowData,
    columns,
    onRowClick,
    onGridReady,
    customComponents,
    fullWidthColumns,
    style,
    hideHeader,
    emptyMessage,
    hideFloatingFilter,
    rowHeight,
    autoHeight,
    pagination,
    onRowCountChange,
    pageSize,
    rowSelection,
    highlightSelectedRow,
    onFirstDataRendered,
    isLoading,
    flushPrevious,
    ignoreClickForFields,
    useFullWidthRows,
    fullWidthCellRenderer,
    rowStyle,
    getRowId
}: GridComponentProps) => {
    const gridApiRef = useRef<GridApi>();
    const [currentRowData, setCurrentRowData] = useState<any[]>(null);
    const currentRowDataRef = useRef<any[]>(null);
    const [isGridReady, setIsGridReady] = useState(false);
    const flushPreviousRows = flushPrevious;

    useEffect(() => {
        currentRowDataRef.current = currentRowData;
    }, [currentRowData]);

    const handleGridReady = (api: GridApi) => {
        gridApiRef.current = api;
        onGridReady && onGridReady(api);
        setIsGridReady(true);
    };

    // const overlayPadding = !hideHeader || !hideFloatingFilter ? (hideHeader && hideFloatingFilter ? 20 : 96) : 0;

    const frameworkComponents = {
        GridCustomDisplay: GridCustomDisplayView,
        GridCellList: GridCellListView,
        GridCheckbox: GridCheckboxView,
        GridSlider: GridSliderView,
        GridCellLargeText: GridCellLargeTextView,
        GridCellDateTime: GridCellDateTimeView,
        GridCellDate: GridCellDateView,
        GridCellLongText: GridCellLongTextView,
        GridCellLargeTextScroll: GridCellLargeTextScrollView,
        GridCellErrorIcon: GridCellErrorIconView,
        LoadingOverlay: () => <LoadIndicatorView size={LoadIndicatorSizeEnum.Medium} style={gridOverlayStyle()} />,
        NoRowsOverlay: () => <div style={gridOverlayStyle()}>{emptyMessage || 'No Data Found'}</div>,
        GridCellYesNo: GridCellYesNoView
    };

    if (customComponents) {
        customComponents.forEach(({ component, name }) => {
            frameworkComponents[name] = component;
        });
    }

    const defaultColDef: ColDef = {
        flex: fullWidthColumns ? 1 : 0,
        autoHeight: !rowHeight,
        filter: !hideFloatingFilter,
        filterParams: {
            suppressAndOrCondition: true
        },
        floatingFilter: !hideFloatingFilter
    };

    const containerStyle: CSSProperties = {
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        ...style
    };

    const handleRowClick = (event: CellClickedEvent) => {
        // check if we should ignore the click for this field
        const ignoreRowClick = ignoreClickForFields?.some((field) => field === event.colDef.field);
        if (ignoreRowClick) {
            return;
        }

        // click was not ignored, send it up
        const data = event.data;
        onRowClick && onRowClick(data);
    };

    const handleFilterChanged = () => {
        onRowCountChange && onRowCountChange(gridApiRef.current.getDisplayedRowCount());
    };

    const isFullWidthRows = useCallback(
        (params: IsFullWidthRowParams) => {
            return useFullWidthRows;
        },
        [useFullWidthRows]
    );

    useEffect(() => {
        if (!rowData || !gridApiRef.current) {
            return;
        }

        setCurrentRowData(rowData);
        currentRowDataRef.current = rowData;

        if (rowData.length <= 0) {
            // force no rows overlay, not automatically happening on 2nd load
            // 1 second delay to help with some of the larger grids since they load at the same time
            setTimeout(() => {
                gridApiRef.current.showNoRowsOverlay();
            }, 1000);
        }
    }, [rowData, isGridReady]);

    return (
        <div style={containerStyle}>
            <div className="ag-theme-alpine" data-test-id={DTIGrid.AgGrid} style={gridContainerStyle}>
                <AgGridReact
                    theme={agGridTheme}
                    domLayout={autoHeight ? 'autoHeight' : 'normal'}
                    loadingOverlayComponent="LoadingOverlay"
                    loading={isLoading}
                    noRowsOverlayComponent="NoRowsOverlay"
                    headerHeight={hideHeader && hideFloatingFilter ? 0 : !hideFloatingFilter || !hideFloatingFilter ? 50 : null}
                    rowData={currentRowData}
                    onGridReady={(event) => handleGridReady(event.api)}
                    onFirstDataRendered={onFirstDataRendered}
                    rowHeight={rowHeight}
                    defaultColDef={defaultColDef}
                    components={frameworkComponents}
                    columnDefs={columns}
                    onCellClicked={handleRowClick}
                    rowStyle={{ ...rowStyle, cursor: onRowClick ? 'pointer' : 'default' }}
                    pagination={pagination}
                    paginationPageSize={pageSize}
                    onFilterChanged={handleFilterChanged}
                    rowSelection={rowSelection}
                    getRowClass={() => {
                        return highlightSelectedRow ? 'grid-highlight-selected' : '';
                    }}
                    suppressRowHoverHighlight={true}
                    includeHiddenColumnsInQuickFilter={true}
                    className={hideHeader && !hideFloatingFilter ? 'ag-grid-hidden-header' : ''}
                    isFullWidthRow={isFullWidthRows}
                    fullWidthCellRenderer={fullWidthCellRenderer}
                    getRowId={getRowId}
                />
            </div>
        </div>
    );
};
